import {
  ABILITIES,
  ARENA_HEIGHT,
  ARENA_WIDTH,
  BOSS_START_MS,
  FRENZY_XP_MULTIPLIER,
  MAX_PLAYERS,
  PLAYER_IDLE_TIMEOUT_MS,
  REVIVE_DURATION_MS,
  type AbilityId,
} from "./constants";
import type { BossPhase, MatchStage } from "./protocol";

export function parseAbility(value: string | null): AbilityId {
  if (ABILITIES.includes(value as AbilityId)) return value as AbilityId;
  return "ember";
}

export function sanitizeName(value: string): string {
  const clean = value
    .replace(/[^\p{L}\p{N} _-]/gu, "")
    .trim()
    .slice(0, 18);
  return clean || "Looter";
}

export function xpForLevel(level: number): number {
  const safeLevel = Math.max(1, level);
  return 60 + safeLevel * 35 + safeLevel * safeLevel * 5;
}

export function stageForElapsed(
  elapsed: number,
  fastMode = false,
): MatchStage {
  if (elapsed >= (fastMode ? 10_000 : BOSS_START_MS)) return "boss";
  if (elapsed >= (fastMode ? 7_000 : 5 * 60_000)) return "chaos";
  if (elapsed >= (fastMode ? 3_500 : 2 * 60_000)) return "escalation";
  return "descent";
}

export function moteDropsAllowedForElapsed(
  elapsed: number,
  bossStartMs = BOSS_START_MS,
): boolean {
  return elapsed >= 0 && elapsed < bossStartMs;
}

export function enemyPopulationScaleForRound(roundNumber: number): number {
  const safeRound = Math.max(1, Math.floor(roundNumber));
  if (safeRound >= 4) return 2;
  if (safeRound >= 2) return 1.5;
  return 1;
}

export function enemyHealthScaleForRound(roundNumber: number): number {
  const safeRound = Math.max(1, Math.floor(roundNumber));
  if (safeRound >= 4) return 2.5;
  if (safeRound === 3) return 2;
  if (safeRound === 2) return 1.5;
  return 1;
}

export function enemyPopulationTarget(
  playerCount: number,
  progress: number,
  roundNumber: number,
): number {
  const safePlayers = Math.max(1, Math.floor(playerCount));
  const safeProgress = clamp(progress, 0, 1.35);
  const baseTarget =
    8 +
    safePlayers * 1.35 +
    safeProgress * (18 + safePlayers * 1.65);
  return Math.min(
    360,
    Math.floor(baseTarget * enemyPopulationScaleForRound(roundNumber)),
  );
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

export function round(value: number, precision = 1): number {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

export function arenaBoundsForPlayers(
  playerCount: number,
): [x: number, y: number, width: number, height: number] {
  const safeCount = clamp(Math.floor(playerCount), 1, MAX_PLAYERS);
  const population = (safeCount - 1) / Math.max(1, MAX_PLAYERS - 1);
  const scale = 0.625 + Math.sqrt(population) * 0.375;
  const width = Math.round(ARENA_WIDTH * scale);
  const height = Math.round(ARENA_HEIGHT * scale);
  return [
    (ARENA_WIDTH - width) / 2,
    (ARENA_HEIGHT - height) / 2,
    width,
    height,
  ];
}

export function reviveProgress(startedAt: number, now: number): number {
  if (startedAt <= 0 || now <= startedAt) return 0;
  return clamp((now - startedAt) / REVIVE_DURATION_MS, 0, 1);
}

export function reviveShieldActive(shieldUntil: number, now: number): boolean {
  return shieldUntil > now;
}

export function lootProtectionActive(
  hasPendingLoot: boolean,
  ghost: boolean,
): boolean {
  return hasPendingLoot && !ghost;
}

export function personalChallengeRefreshReady(
  completed: boolean,
  refreshAt: number,
  now: number,
): boolean {
  return completed && refreshAt > 0 && now >= refreshAt;
}

export function playerIdleTimeoutReached(
  stationarySince: number,
  now: number,
  timeoutMs = PLAYER_IDLE_TIMEOUT_MS,
): boolean {
  return (
    stationarySince > 0 &&
    now >= stationarySince &&
    now - stationarySince >= timeoutMs
  );
}

export function untargetedEnemyDestination(
  enemyId: number,
  now: number,
  arena = arenaBoundsForPlayers(MAX_PLAYERS),
): { x: number; y: number } {
  const [arenaX, arenaY, arenaWidth, arenaHeight] = arena;
  const phase = enemyId * 2.39996 + now / 5_200;
  const radiusX = arenaWidth * (0.16 + (enemyId % 5) * 0.025);
  const radiusY = arenaHeight * (0.18 + (enemyId % 4) * 0.03);
  return {
    x: arenaX + arenaWidth / 2 + Math.cos(phase) * radiusX,
    y: arenaY + arenaHeight / 2 + Math.sin(phase * 1.17) * radiusY,
  };
}

export function sessionPlayerIsReclaimable(
  connected: boolean,
  disconnectedAt: number,
  now: number,
): boolean {
  return (
    !connected &&
    disconnectedAt > 0 &&
    now - disconnectedAt <= 20_000
  );
}

export function consumeUltimateBarrageCharge(
  charges: number,
  now: number,
  cooldownMs: number,
): { remaining: number; readyAt: number } {
  const remaining = Math.max(0, Math.floor(charges) - 1);
  return {
    remaining,
    readyAt: remaining > 0 ? now : now + Math.max(0, cooldownMs),
  };
}

export function partyWiped(
  players: ReadonlyArray<{ connected: boolean; ghost: boolean }>,
): boolean {
  const connectedPlayers = players.filter((player) => player.connected);
  return (
    connectedPlayers.length > 0 &&
    connectedPlayers.every((player) => player.ghost)
  );
}

export function frenzyXpMultiplier(overdriveUntil: number, now: number): number {
  return overdriveUntil > now ? FRENZY_XP_MULTIPLIER : 1;
}

export function moteGoalForPlayers(playerCount: number, fastMode = false): number {
  const safePlayers = Math.max(1, Math.min(75, Math.floor(playerCount)));
  if (fastMode) return 4 + Math.ceil(safePlayers * 0.05);
  return Math.ceil(14 + 4 * safePlayers ** 0.82);
}

export function triangleZoneCount(playerCount: number): number {
  const safePlayers = Math.max(0, Math.floor(playerCount));
  if (safePlayers < 2) return 0;
  return safePlayers === 2 ? 2 : 3;
}

export function teamComboRequiredPlayers(playerCount: number): number {
  return Math.max(1, Math.floor(playerCount)) === 1 ? 1 : 2;
}

export function armyWaveHitsPoint(
  point: { x: number; y: number },
  wallPosition: number,
  vertical: boolean,
  gapCenter: number,
  gapSize: number,
  wallWidth: number,
): boolean {
  const crossingDistance = Math.abs(
    (vertical ? point.x : point.y) - wallPosition,
  );
  const gapDistance = Math.abs(
    (vertical ? point.y : point.x) - gapCenter,
  );
  return crossingDistance <= wallWidth / 2 && gapDistance > gapSize / 2;
}

export function bossPhaseForHealthRatio(healthRatio: number): BossPhase {
  if (healthRatio <= 0.25) return 4;
  if (healthRatio <= 0.5) return 3;
  if (healthRatio <= 0.75) return 2;
  return 1;
}

export function raidUltimateRequiredVotes(playerCount: number): number {
  return Math.max(1, Math.floor(Math.max(0, playerCount) / 2) + 1);
}

export function isAbilityReaction(a: AbilityId, b: AbilityId): boolean {
  if (a === b) return false;
  const pair = new Set<AbilityId>([a, b]);
  return (
    (pair.has("frost") && pair.has("solar")) ||
    (pair.has("void") && pair.has("chain")) ||
    (pair.has("bard") && pair.has("necro"))
  );
}

export function oneWayLaserHitsPoint(
  point: { x: number; y: number },
  origin: { x: number; y: number },
  angle: number,
  length: number,
  halfWidth: number,
): boolean {
  const dx = point.x - origin.x;
  const dy = point.y - origin.y;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const along = cos * dx + sin * dy;
  const perpendicular = Math.abs(-sin * dx + cos * dy);
  return along >= 0 && along <= length && perpendicular <= halfWidth;
}
